import type {BlogPost, Category, City, Guide, Media, Walk, Waypoint} from 'types/payload-types';

const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL;
const CENTS_PER_UNIT = 100;
const MINUTES_PER_HOUR = 60;
const RATING_DECIMALS = 1;
const MIN_RATING = 1;
const MAX_RATING = 5;
const MIN_NAME_PARTS = 2;
const URL_PATTERN = /^https?:\/\//u;

/** Flattened, serializable review data used to build `aggregateRating`/`review` JSON-LD nodes and the visible reviews UI. */
export interface WalkReviewSummary {
    authorName: string;
    createdAt: string;
    id: string;
    rating: number;
    text?: string | null;
}

/**
 * Abbreviates a reviewer's full name to the first name plus the last name's initial
 * (e.g. `"Max Mustermann"` -> `"Max M."`) so full names are not published on the site.
 * Names without a separate last name are returned unchanged.
 *
 * @param fullName The reviewer's full name.
 * @returns The abbreviated display name.
 */
export const abbreviateReviewerName = (fullName: string): string => {
    const parts = fullName.trim().split(/\s+/u);

    if (parts.length < MIN_NAME_PARTS) {
        return fullName;
    }

    const lastInitial = parts[parts.length - 1].charAt(0);

    return `${parts[0]} ${lastInitial}.`;
};

/**
 * Builds a BreadcrumbList JSON-LD node (no @context, for use inside @graph).
 *
 * @param items Ordered breadcrumb items with name and url.
 * @returns BreadcrumbList schema object.
 */
export const buildBreadcrumbJsonLd = (items: {name: string; url: string}[]) => ({
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
        '@type': 'ListItem',
        item: item.url,
        name: item.name,
        position: i + 1
    }))
});

/**
 * Converts minutes to an ISO 8601 duration string.
 *
 * @param minutes Duration in minutes.
 * @returns ISO 8601 duration string.
 */
const toIsoDuration = (minutes: number) => {
    const h = Math.floor(minutes / MINUTES_PER_HOUR);
    const m = minutes % MINUTES_PER_HOUR;

    if (h > 0 && m > 0) return `PT${h}H${m}M`;
    if (h > 0) return `PT${h}H`;

    return `PT${m}M`;
};

/**
 * Resolves a Payload relation to the populated object or null.
 *
 * @param ref String ID or populated object.
 * @returns The populated object or null.
 */
const resolveRef = <T extends {id: string}>(ref: T | string | null | undefined): T | null => {
    if (!ref || typeof ref === 'string') return null;

    return ref;
};

/**
 * Gets the absolute URL of a media object's best-available image.
 *
 * @param media Populated Media object.
 * @returns Absolute image URL or null.
 */
export const resolveImageUrl = (media: Media | null | undefined): string | null => {
    if (!media) return null;

    const url = media.sizes?.card?.url ?? media.url;

    if (!url) return null;

    return url.startsWith('http') ? url : `${BASE_URL}${url}`;
};

/** Terrain key → human-readable label map. */
const TERRAIN_LABELS: Record<string, string> = {
    asphalt: 'Asphalt',
    cobblestone: 'Kopfsteinpflaster',
    'forest-path': 'Waldweg',
    gravel: 'Schotter',
    mixed: 'Gemischt'
};

/**
 * Builds additional schema.org PropertyValue entries for walk metadata
 * that has no dedicated Product/TouristTrip property.
 *
 * @param walk The walk document.
 * @returns Array of PropertyValue objects (may be empty).
 */
const buildAdditionalProperties = (walk: Walk): Record<string, unknown>[] => {
    const props: Record<string, unknown>[] = [];

    if (walk.general?.duration) {
        props.push({
            '@type': 'PropertyValue',
            name: 'Dauer',
            value: toIsoDuration(walk.general.duration)
        });
    }

    if (walk.general?.distance) {
        props.push({
            '@type': 'PropertyValue',
            name: 'Distanz',
            value: `${walk.general.distance} km`
        });
    }

    if (walk.general?.terrain && walk.general.terrain.length > 0) {
        const labels = walk.general.terrain.map(t => TERRAIN_LABELS[t] ?? t).join(', ');

        props.push({
            '@type': 'PropertyValue',
            name: 'Untergrund',
            value: labels
        });
    }

    return props;
};

/**
 * Resolves the absolute image URLs for a walk's cover image and gallery,
 * for use as the `image` array of a Product/TouristTrip JSON-LD node.
 *
 * @param walk The full walk document with populated relations.
 * @returns Array of absolute image URLs (cover image first, then gallery).
 */
const resolveWalkImageUrls = (walk: Walk): string[] => {
    const coverImage = resolveRef<Media>(walk.coverImage);
    const galleryImages = (walk.gallery ?? [])
        .map(item => resolveRef<Media>(item.image))
        .filter(Boolean);

    return [coverImage, ...galleryImages]
        .filter(Boolean)
        .map(resolveImageUrl)
        .filter(Boolean);
};

/**
 * Resolves a walk's price as a decimal string (in EUR), e.g. "5.00". Free walks resolve to "0.00".
 *
 * @param walk The walk document.
 * @returns Decimal price string.
 */
const resolveWalkPrice = (walk: Walk): string => {
    const priceCents = walk.pricing?.isFree ? 0 : (walk.pricing?.price ?? 0);

    return (priceCents / CENTS_PER_UNIT).toFixed(2);
};

/**
 * Inline brand node for walk Product entries. A walking tour has no GTIN (that's a barcode
 * system for physical retail goods), so Google's Merchant Listing "global identifier"
 * requirement is satisfied via `brand` plus a self-assigned `mpn` on each Product instead.
 */
const WALK_BRAND = {
    '@type': 'Organization',
    name: 'little walks'
};

/**
 * Inline shipping details for walk Product offers. A walk is unlocked digitally (no physical
 * shipment), so this factually declares zero-cost, zero-day (instant) digital delivery rather
 * than omitting `shippingDetails` entirely.
 */
const WALK_SHIPPING_DETAILS = {
    '@type': 'OfferShippingDetails',
    deliveryTime: {
        '@type': 'ShippingDeliveryTime',
        handlingTime: {
            '@type': 'QuantitativeValue',
            maxValue: 0,
            minValue: 0,
            unitCode: 'DAY'
        },
        transitTime: {
            '@type': 'QuantitativeValue',
            maxValue: 0,
            minValue: 0,
            unitCode: 'DAY'
        }
    },
    shippingRate: {
        '@type': 'MonetaryAmount',
        currency: 'EUR',
        value: '0'
    }
};

/**
 * Inline return policy for walk Product offers. Digital content is excluded from the right of
 * withdrawal once access has been granted (§ 356 Abs. 5 BGB), so purchases are non-returnable.
 */
const WALK_RETURN_POLICY = {
    '@type': 'MerchantReturnPolicy',
    applicableCountry: 'DE',
    returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted'
};

/**
 * Builds an AggregateRating JSON-LD node from a walk's approved reviews.
 *
 * @param reviews Approved reviews for the walk (must be non-empty).
 * @returns AggregateRating schema object.
 */
const buildAggregateRating = (reviews: {rating: number}[]): Record<string, unknown> => ({
    '@type': 'AggregateRating',
    bestRating: MAX_RATING,
    ratingCount: reviews.length,
    ratingValue: (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(RATING_DECIMALS),
    worstRating: MIN_RATING
});

/**
 * Builds Review JSON-LD nodes from a walk's approved reviews.
 *
 * @param reviews Approved reviews for the walk.
 * @returns Array of Review schema objects.
 */
const buildReviewNodes = (reviews: WalkReviewSummary[]): Record<string, unknown>[] => reviews.map(review => ({
    '@type': 'Review',
    author: {
        '@type': 'Person',
        name: review.authorName
    },
    datePublished: review.createdAt,
    reviewBody: review.text ?? undefined,
    reviewRating: {
        '@type': 'Rating',
        bestRating: MAX_RATING,
        ratingValue: review.rating,
        worstRating: MIN_RATING
    }
}));

/**
 * Builds the `aggregateRating`/`review` JSON-LD fields from a walk's approved reviews.
 * Returns an empty object when there are no approved reviews, since Google requires
 * `aggregateRating` to reflect actual review data rather than being fabricated.
 *
 * @param reviews Approved reviews for the walk.
 * @returns Partial JSON-LD fields to merge into the Product node (may be empty).
 */
const buildReviewFields = (reviews: WalkReviewSummary[]): Record<string, unknown> => {
    if (reviews.length === 0) return {};

    return {
        aggregateRating: buildAggregateRating(reviews),
        review: buildReviewNodes(reviews)
    };
};

/**
 * Builds a Product + TouristTrip JSON-LD node for a walk detail page.
 * Does NOT include @context — meant to be used inside a @graph array.
 *
 * @param walk    The full walk document with populated relations.
 * @param reviews Approved reviews for the walk, used for `aggregateRating`/`review`.
 * @returns JSON-LD object ready for injection inside @graph.
 */
const buildWalkNode = (walk: Walk, reviews: WalkReviewSummary[]): Record<string, unknown> => {
    const pageUrl = `${BASE_URL}/walks/${walk.slug}`;
    const city = resolveRef<City>(walk.city);
    const guide = resolveRef<Guide>(walk.guide);
    const imageUrls = resolveWalkImageUrls(walk);

    const categories = (walk.categories ?? [])
        .map(c => resolveRef<Category>(c))
        .filter(Boolean)
        .map(c => c.name);

    const waypoints = (walk.waypoints?.docs ?? [])
        .map(wp => resolveRef<Waypoint>(wp))
        .filter(Boolean);

    const jsonLd: Record<string, unknown> = {
        '@id': `${pageUrl}#product`,
        '@type': ['Product', 'TouristTrip'],
        brand: WALK_BRAND,
        description: walk.meta?.description ?? walk.shortDescription,
        inLanguage: 'de',
        mainEntityOfPage: {'@id': `${pageUrl}#webpage`},
        mpn: walk.slug,
        name: walk.meta?.title ?? walk.title,
        offers: {
            '@type': 'Offer',
            availability: 'https://schema.org/InStock',
            hasMerchantReturnPolicy: WALK_RETURN_POLICY,
            price: resolveWalkPrice(walk),
            priceCurrency: 'EUR',
            shippingDetails: WALK_SHIPPING_DETAILS,
            url: pageUrl
        },
        url: pageUrl,
        ...buildReviewFields(reviews)
    };

    if (imageUrls.length > 0) {
        jsonLd.image = imageUrls;
    }

    if (categories.length > 0) {
        jsonLd.touristType = categories;
    }

    const additionalProps = buildAdditionalProperties(walk);

    if (additionalProps.length > 0) {
        jsonLd.additionalProperty = additionalProps;
    }

    if (city) {
        const place: Record<string, unknown> = {
            '@type': 'Place',
            name: city.name
        };

        // city.location is GeoJSON [lng, lat]
        if (city.location) {
            place.geo = {
                '@type': 'GeoCoordinates',
                latitude: city.location[1],
                longitude: city.location[0]
            };
        }

        jsonLd.contentLocation = place;
    }

    if (guide) {
        const guideUrl = `${BASE_URL}/guides/${guide.slug}`;
        const authorUrl = guide.website && URL_PATTERN.test(guide.website) ? guide.website : guideUrl;

        jsonLd.author = {
            '@type': 'Person',
            name: guide.name,
            url: authorUrl
        };
    }

    if (waypoints.length > 0) {
        jsonLd.itinerary = {
            '@type': 'ItemList',
            itemListElement: waypoints.map((wp, i) => {
                const item: Record<string, unknown> = {
                    '@type': 'TouristAttraction',
                    description: wp.shortDescription ?? undefined,
                    name: wp.title
                };

                // wp.location is GeoJSON [lng, lat]
                if (wp.location) {
                    item.geo = {
                        '@type': 'GeoCoordinates',
                        latitude: wp.location[1],
                        longitude: wp.location[0]
                    };
                }

                return {
                    '@type': 'ListItem',
                    item,
                    position: i + 1
                };
            }),
            numberOfItems: waypoints.length
        };
    }

    return jsonLd;
};

/**
 * Builds an `ItemPage` WebPage-subtype node identifying the walk detail page itself, linked to
 * the Product node via `mainEntity`. This carries no rich-result of its own, but helps Google
 * unambiguously associate the page with its entity, which supports all other rich results.
 *
 * @param walk    The walk document.
 * @param pageUrl The walk's canonical detail page URL.
 * @returns ItemPage schema object.
 */
const buildWalkItemPageNode = (walk: Walk, pageUrl: string): Record<string, unknown> => ({
    '@id': `${pageUrl}#webpage`,
    '@type': 'ItemPage',
    inLanguage: 'de',
    isPartOf: {'@id': `${BASE_URL}/#website`},
    mainEntity: {'@id': `${pageUrl}#product`},
    name: walk.meta?.title ?? walk.title,
    url: pageUrl
});

/**
 * Builds a complete @graph JSON-LD document for a walk detail page.
 * Contains Organization, Product/TouristTrip, ItemPage, and BreadcrumbList in one block.
 *
 * @param walk        The full walk document with populated relations.
 * @param breadcrumbs Ordered breadcrumb items.
 * @param reviews     Approved reviews for the walk, used for `aggregateRating`/`review` (defaults to none).
 * @returns Single JSON-LD object with @graph array.
 */
export const buildWalkJsonLd = (
    walk: Walk,
    breadcrumbs: {name: string; url: string}[],
    reviews: WalkReviewSummary[] = []
): Record<string, unknown> => ({
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@id': `${BASE_URL}/#organization`,
            '@type': 'Organization',
            name: 'little walks',
            url: BASE_URL
        },
        buildWalkNode(walk, reviews),
        buildWalkItemPageNode(walk, `${BASE_URL}/walks/${walk.slug}`),
        buildBreadcrumbJsonLd(breadcrumbs)
    ]
});

/**
 * Builds a FAQPage JSON-LD node from question/answer items.
 * Does NOT include @context — meant to be used inside a @graph array.
 *
 * @param items FAQ question/answer pairs.
 * @returns FAQPage schema object.
 */
const buildFaqPageNode = (items: {answer: string; question: string}[]): Record<string, unknown> => ({
    '@type': 'FAQPage',
    mainEntity: items.map(item => ({
        '@type': 'Question',
        acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer
        },
        name: item.question
    }))
});

/**
 * Builds a complete @graph JSON-LD document for the FAQ page.
 * Contains Organization, FAQPage, and BreadcrumbList in one block.
 *
 * @param items       FAQ question/answer pairs.
 * @param breadcrumbs Ordered breadcrumb items.
 * @returns Single JSON-LD object with @graph array.
 */
export const buildFaqJsonLd = (
    items: {answer: string; question: string}[],
    breadcrumbs: {name: string; url: string}[]
): Record<string, unknown> => ({
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@id': `${BASE_URL}/#organization`,
            '@type': 'Organization',
            name: 'little walks',
            url: BASE_URL
        },
        buildFaqPageNode(items),
        buildBreadcrumbJsonLd(breadcrumbs)
    ]
});

/**
 * Builds a Product summary JSON-LD node for a walk, for use as a `ListItem.item` on the
 * walks overview page. Unlike {@link buildWalkNode}, this omits fields only relevant to the
 * walk's own detail page (itinerary, contentLocation, etc.) and only includes what Google's
 * structured-data-carousels (beta) feature reads for Product items: name, image, url, brand,
 * mpn, description, and the recommended `offers`/`aggregateRating`.
 *
 * @param walk    The walk document (cover image and gallery populated).
 * @param reviews Approved reviews for the walk, used for the recommended `aggregateRating`.
 * @returns Product schema object.
 */
const buildWalkListItem = (walk: Walk, reviews: {rating: number}[]): Record<string, unknown> => {
    const imageUrls = resolveWalkImageUrls(walk);

    const product: Record<string, unknown> = {
        '@type': 'Product',
        brand: WALK_BRAND,
        description: walk.meta?.description ?? walk.shortDescription,
        mpn: walk.slug,
        name: walk.meta?.title ?? walk.title,
        offers: {
            '@type': 'Offer',
            availability: 'https://schema.org/InStock',
            hasMerchantReturnPolicy: WALK_RETURN_POLICY,
            price: resolveWalkPrice(walk),
            priceCurrency: 'EUR',
            shippingDetails: WALK_SHIPPING_DETAILS
        },
        url: `${BASE_URL}/walks/${walk.slug}`
    };

    if (imageUrls.length > 0) {
        product.image = imageUrls;
    }

    if (reviews.length > 0) {
        product.aggregateRating = buildAggregateRating(reviews);
    }

    return product;
};

/**
 * Builds an ItemList JSON-LD object for a walks overview / catalog page. Each item is a full
 * Product node (not just a URL reference) so the page is eligible for Google's structured-data-
 * carousels (beta) feature, which renders a swipeable carousel of Product tiles with image,
 * price, and rating — currently rolled out in EEA countries, Turkey, and South Africa.
 *
 * @param walks           Array of walk documents (cover image and gallery populated).
 * @param reviewsByWalkId Approved reviews grouped by walk ID, used for `aggregateRating` (defaults to none).
 * @returns ItemList JSON-LD object.
 */
export const buildWalkListJsonLd = (
    walks: Walk[],
    reviewsByWalkId: Record<string, {rating: number}[]> = {}
): Record<string, unknown> => ({
    '@id': `${BASE_URL}/walks#itemlist`,
    '@type': 'ItemList',
    itemListElement: walks.map((walk, i) => ({
        '@type': 'ListItem',
        item: buildWalkListItem(walk, reviewsByWalkId[walk.id] ?? []),
        position: i + 1
    })),
    mainEntityOfPage: {'@id': `${BASE_URL}/walks#webpage`},
    numberOfItems: walks.length
});

/**
 * Builds a `CollectionPage` WebPage-subtype node identifying a summary or catalog page, linked
 * to its main entity (such as an ItemList) via `mainEntity`. This carries no rich result of its
 * own, but helps Google unambiguously associate the page with its entity, which supports all
 * other rich results, such as the structured-data-carousels beta feature.
 *
 * @param page         The page's name and canonical URL.
 * @param page.name    The page's display name.
 * @param page.url     The page's canonical URL.
 * @param mainEntityId The `@id` of this page's main entity, such as an ItemList.
 * @returns CollectionPage schema object.
 */
export const buildCollectionPageJsonLd = (
    page: {name: string; url: string},
    mainEntityId: string
): Record<string, unknown> => ({
    '@id': `${page.url}#webpage`,
    '@type': 'CollectionPage',
    inLanguage: 'de',
    isPartOf: {'@id': `${BASE_URL}/#website`},
    mainEntity: {'@id': mainEntityId},
    name: page.name,
    url: page.url
});

/**
 * Builds a BlogPosting JSON-LD node for a blog detail page.
 * Does NOT include @context — meant to be used inside a @graph array.
 *
 * @param post The full BlogPost document with populated relations.
 * @returns BlogPosting schema object.
 */
const buildBlogPostingNode = (post: BlogPost): Record<string, unknown> => {
    const pageUrl = `${BASE_URL}/blog/${post.slug}`;
    const author = resolveRef<Guide>(post.author);
    const imageUrl = resolveImageUrl(resolveRef<Media>(post.headerImage));

    const jsonLd: Record<string, unknown> = {
        '@id': `${pageUrl}#blogposting`,
        '@type': 'BlogPosting',
        dateModified: post.updatedAt,
        datePublished: post.publishedAt ?? post.createdAt,
        description: post.meta?.description ?? post.description,
        headline: post.meta?.title ?? post.title,
        inLanguage: 'de',
        isPartOf: {'@id': `${BASE_URL}/blog#blog`},
        mainEntityOfPage: {'@id': `${pageUrl}#webpage`},
        publisher: {'@id': `${BASE_URL}/#organization`},
        url: pageUrl
    };

    if (imageUrl) {
        jsonLd.image = imageUrl;
    }

    if (author) {
        jsonLd.author = {
            '@type': 'Person',
            name: author.name,
            url: `${BASE_URL}/guides/${author.slug}`
        };
    }

    return jsonLd;
};

/**
 * Builds an `ItemPage` WebPage-subtype node identifying the blog detail page itself, linked to
 * the BlogPosting node via `mainEntity` (same pattern as {@link buildWalkItemPageNode}).
 *
 * @param post    The BlogPost document.
 * @param pageUrl The post's canonical detail page URL.
 * @returns ItemPage schema object.
 */
const buildBlogItemPageNode = (post: BlogPost, pageUrl: string): Record<string, unknown> => ({
    '@id': `${pageUrl}#webpage`,
    '@type': 'ItemPage',
    inLanguage: 'de',
    isPartOf: {'@id': `${BASE_URL}/#website`},
    mainEntity: {'@id': `${pageUrl}#blogposting`},
    name: post.meta?.title ?? post.title,
    url: pageUrl
});

/**
 * Builds a complete @graph JSON-LD document for a blog detail page.
 * Contains Organization, BlogPosting, ItemPage, and BreadcrumbList in one block.
 *
 * @param post        The full BlogPost document with populated relations.
 * @param breadcrumbs Ordered breadcrumb items.
 * @returns Single JSON-LD object with @graph array.
 */
export const buildBlogPostingJsonLd = (
    post: BlogPost,
    breadcrumbs: {name: string; url: string}[]
): Record<string, unknown> => ({
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@id': `${BASE_URL}/#organization`,
            '@type': 'Organization',
            name: 'little walks',
            url: BASE_URL
        },
        buildBlogPostingNode(post),
        buildBlogItemPageNode(post, `${BASE_URL}/blog/${post.slug}`),
        buildBreadcrumbJsonLd(breadcrumbs)
    ]
});

/**
 * Builds a BlogPosting summary JSON-LD node for use as a `ListItem.item` on the blog overview
 * page (same pattern as {@link buildWalkListItem}).
 *
 * @param post The BlogPost document (header image populated).
 * @returns BlogPosting summary schema object.
 */
const buildBlogListItem = (post: BlogPost): Record<string, unknown> => {
    const author = resolveRef<Guide>(post.author);
    const imageUrl = resolveImageUrl(resolveRef<Media>(post.headerImage));

    const item: Record<string, unknown> = {
        '@type': 'BlogPosting',
        headline: post.meta?.title ?? post.title,
        url: `${BASE_URL}/blog/${post.slug}`
    };

    if (imageUrl) {
        item.image = imageUrl;
    }

    if (post.publishedAt) {
        item.datePublished = post.publishedAt;
    }

    if (author) {
        item.author = {
            '@type': 'Person',
            name: author.name,
            url: `${BASE_URL}/guides/${author.slug}`
        };
    }

    return item;
};

/**
 * Builds an ItemList JSON-LD object for the blog overview page (same pattern as
 * {@link buildWalkListJsonLd}).
 *
 * @param posts Array of BlogPost documents (header image populated), newest first.
 * @returns ItemList JSON-LD object.
 */
export const buildBlogListJsonLd = (posts: BlogPost[]): Record<string, unknown> => ({
    '@id': `${BASE_URL}/blog#itemlist`,
    '@type': 'ItemList',
    itemListElement: posts.map((post, i) => ({
        '@type': 'ListItem',
        item: buildBlogListItem(post),
        position: i + 1
    })),
    mainEntityOfPage: {'@id': `${BASE_URL}/blog#webpage`},
    numberOfItems: posts.length
});

/**
 * Builds a complete @graph JSON-LD document for a generic content page (such as a block-based
 * Landingpage) that has no dedicated schema.org entity of its own: Organization, a plain WebPage
 * node, and BreadcrumbList.
 *
 * @param page             The page's name, canonical URL, and optional description.
 * @param page.description Optional meta description for the WebPage node.
 * @param page.name        The page's display name.
 * @param page.url         The page's canonical URL.
 * @param breadcrumbs      Ordered breadcrumb items.
 * @returns Single JSON-LD object with @graph array.
 */
export const buildWebPageJsonLd = (
    page: {description?: string | null; name: string; url: string},
    breadcrumbs: {name: string; url: string}[]
): Record<string, unknown> => ({
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@id': `${BASE_URL}/#organization`,
            '@type': 'Organization',
            name: 'little walks',
            url: BASE_URL
        },
        {
            '@id': `${page.url}#webpage`,
            '@type': 'WebPage',
            description: page.description ?? undefined,
            inLanguage: 'de',
            isPartOf: {'@id': `${BASE_URL}/#website`},
            name: page.name,
            url: page.url
        },
        buildBreadcrumbJsonLd(breadcrumbs)
    ]
});